import { Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useCoreStore } from '../core/store';
import { AppCoreEnums } from 'src/core/shared/enums';
import { httpClient } from 'src/core/axios/http-client';
import { IPagedList, Pagination } from '../core/models';
import { IProfilesState, Profile, Permission, PermissionType } from './models';
import { ResolveParams, ResolveResult } from 'src/core/models/routing';
import updatePaginationStateData from 'src/core/composable/use-pagination-utils';
import useContainerFiltersResolveParams from '../../core/composable/containers/use-container-filters-resolve-params';

const useStoreActions = (state: Ref<IProfilesState>) => {
  const route = useRoute();
  const coreStore = useCoreStore();
  const { buildResolveParamsFilters } = useContainerFiltersResolveParams();

  const URL_PREFIX = 'profiles';
  const loadProfiles = (resolveParams: ResolveParams) => {
    const filters = buildResolveParamsFilters(resolveParams);
    return httpClient.post<void, IPagedList<Profile>>(`${URL_PREFIX}`, filters).then((response) => {
      state.value.profiles = response.dataList;
      return new ResolveResult(filters.pagination, response, resolveParams);
    });
  };

  const loadProfileById = (resolveParams: ResolveParams) => {
    return httpClient.get<void, Profile>(`${URL_PREFIX}/${resolveParams.params.id}`).then((response) => {
      state.value.profileOverview = response;
      state.value.profileProperties = response;
    });
  };

  const loadProfilePermissions = (payload: ResolveParams) => {
    const filters = buildResolveParamsFilters(payload);
    return httpClient
      .post<void, IPagedList<Permission>>(`${URL_PREFIX}/${payload.params.id}/permissions`, filters)
      .then((response) => {
        state.value.profilePermissions = response.dataList;
        return new ResolveResult(filters.pagination, response, payload);
      });
  };

  const saveProfile = (payload: Profile) => {
    return httpClient.post<void, Profile>(`${URL_PREFIX}/save`, payload).then((response) => response);
  };

  const deleteProfiles = (ids: number[]) => {
    return coreStore.deleteEntities('profile', ids);
  };

  const loadProfileAvailablePermissions = (payload: ResolveParams) => {
    const filters = buildResolveParamsFilters(payload);
    return httpClient
      .post<void, IPagedList<Permission>>(`${URL_PREFIX}/${payload.params.id}/permissions/available`, filters)
      .then((response) => {
        state.value.availablePermissions = response.dataList;

        updatePaginationStateData(
          { ...filters.pagination, rowsNumber: response.totalRecords } as Pagination,
          AppCoreEnums.ContainerType.DrawerContainer
        );
      });
  };

  const removePermissionsFromProfile = () => {
    const idProfile = route.params.id;
    const permissionIds = coreStore.tableContainerData.selectedKeys
      .map((x) => Number(x))
      .filter((x) => Number.isInteger(x) && x > 0);
    return httpClient.post(`${URL_PREFIX}/${idProfile}/permissions/remove`, permissionIds);
  };

  const assignPermissionsToProfile = () => {
    const idProfile = route.params.id;
    const permissionIds = coreStore.drawerContainerData.selectedKeys
      .map((x) => Number(x))
      .filter((x) => Number.isInteger(x) && x > 0);
    return httpClient.post(`${URL_PREFIX}/${idProfile}/permissions/assign`, permissionIds);
  };

  const getPermissionTypes = () => {
    return httpClient.post<void,PermissionType[]>(`${URL_PREFIX}/get-permission-types`).then((response) => 
      state.value.permissionTypes= response
    )
  }

  return {
    saveProfile,
    loadProfiles,
    deleteProfiles,
    loadProfileById,
    getPermissionTypes,
    loadProfilePermissions,
    assignPermissionsToProfile,
    removePermissionsFromProfile,
    loadProfileAvailablePermissions,
  };
};

export default useStoreActions;
